import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, Target, FlaskConical, Building, Handshake, Leaf } from "lucide-react";
import Reveal from "../components/Reveal";
import { coreValues, founders } from "../mock/data";

const iconMap = { Target, FlaskConical, Building, Handshake, Leaf };

const aboutImage = "https://images.unsplash.com/photo-1527956041665-d7a1b380c460";
const teamImage = "https://images.unsplash.com/photo-1517048676732-d65bc937f952";

const PageHero = ({ eyebrow, title, accent }) => (
  <section className="relative overflow-hidden" style={{ paddingTop: 160, paddingBottom: 72 }}>
    <div className="glow-orb" style={{ width: 380, height: 380, background: "var(--oo-orange)", top: "-10%", left: "-6%", opacity: 0.18 }} />
    <div className="absolute inset-0 grid-overlay" style={{ opacity: 0.35 }} />
    <div className="oo-container relative">
      <Reveal>
        <span className="oo-eyebrow">{eyebrow}</span>
      </Reveal>
      <Reveal delay={80}>
        <h1 className="font-display font-extrabold" style={{ fontSize: "clamp(38px,6vw,76px)", lineHeight: 1.03, marginTop: 20, letterSpacing: "-0.02em", maxWidth: 900 }}>
          {title} <span className="text-accent">{accent}</span>
        </h1>
      </Reveal>
    </div>
  </section>
);

const About = () => {
  return (
    <main>
      <PageHero eyebrow="Est. November 2019" title="We Build Media" accent="That Lasts" />

      {/* STORY */}
      <section style={{ paddingBottom: 96 }}>
        <div className="oo-container">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <div style={{ overflow: "hidden", borderRadius: 24, border: "1px solid var(--oo-border)" }}>
                <img src={aboutImage} alt="City infrastructure" className="w-full object-cover" style={{ aspectRatio: "4/3" }} />
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="space-y-5" style={{ color: "var(--oo-muted)", fontSize: 16, lineHeight: 1.8 }}>
                <p>
                  OOHMA Multimedia was founded in November 2019 with a single conviction: that out-of-home advertising in India deserved better infrastructure, smarter monetisation, and more purposeful design. What began as a media aggregation platform has grown into an end-to-end infrastructure and media company operating across cities, school networks, and digital platforms.
                </p>
                <p>
                  Headquartered in Dwarka, New Delhi, we act as a bridge between media owners, brands, agencies, and city bodies — orchestrating campaigns that don't just reach audiences, but become part of the urban environment.
                </p>
                <p>
                  From concept to execution, we control every layer — design, fabrication, media buying, monitoring, and impact reporting. This integrated approach ensures quality at scale and accountability at every step.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="oo-section" style={{ background: "var(--oo-bg-2)" }}>
        <div className="oo-container">
          <Reveal>
            <h2 className="font-display font-extrabold text-center" style={{ fontSize: "clamp(30px,4.5vw,48px)", letterSpacing: "-0.02em" }}>
              Our Core <span className="text-accent">Values</span>
            </h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5" style={{ marginTop: 52 }}>
            {coreValues.map((v, i) => {
              const Icon = iconMap[v.icon];
              return (
                <Reveal key={v.title} delay={i * 70}>
                  <div className="oo-card h-full text-center" style={{ padding: "36px 20px" }}>
                    <div className="inline-flex items-center justify-center" style={{ width: 60, height: 60, borderRadius: 18, background: "var(--oo-orange-soft)" }}>
                      <Icon size={26} className="text-accent" />
                    </div>
                    <h3 className="font-display" style={{ fontSize: 17, fontWeight: 600, marginTop: 20 }}>{v.title}</h3>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="oo-section">
        <div className="oo-container">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <Reveal>
              <div style={{ overflow: "hidden", borderRadius: 24, border: "1px solid var(--oo-border)" }}>
                <img src={teamImage} alt="Leadership team" className="w-full object-cover" style={{ aspectRatio: "3/4" }} />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <span className="oo-eyebrow">Founders & Leadership</span>
                <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(30px,4.5vw,48px)", letterSpacing: "-0.02em", marginTop: 16 }}>
                  The People Behind <span className="text-accent">OOHMA</span>
                </h2>
              </Reveal>
              <div className="space-y-5" style={{ marginTop: 36 }}>
                {founders.map((f, i) => (
                  <Reveal key={f.name} delay={i * 90}>
                    <div className="oo-card" style={{ padding: 28 }}>
                      <h3 className="font-display" style={{ fontSize: 22, fontWeight: 700 }}>{f.name}</h3>
                      <p className="text-accent" style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{f.role}</p>
                      <p style={{ color: "var(--oo-muted)", marginTop: 12, lineHeight: 1.7 }}>{f.detail}</p>
                      <div className="flex flex-wrap gap-5" style={{ marginTop: 16 }}>
                        {f.phone && (
                          <a href={`tel:${f.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 hover-underline" style={{ color: "var(--oo-muted)", fontSize: 14 }}>
                            <Phone size={15} className="text-accent" /> {f.phone}
                          </a>
                        )}
                        <a href={`mailto:${f.email}`} className="inline-flex items-center gap-2 hover-underline" style={{ color: "var(--oo-muted)", fontSize: 14 }}>
                          <Mail size={15} className="text-accent" /> {f.email}
                        </a>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ paddingBottom: 110 }}>
        <div className="oo-container text-center">
          <Reveal>
            <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(30px,4.5vw,52px)", letterSpacing: "-0.02em" }}>
              Ready to <span className="text-accent">Collaborate?</span>
            </h2>
            <Link to="/contact" className="btn-primary" style={{ marginTop: 30 }}>
              Get in Touch <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default About;
